export type GranularConsent = {
  analytics: boolean;
  marketing: boolean;
};

export const CONSENT_STORAGE_KEY_V2 = "fastsell_consent_v2";
/** @deprecated migrated on read */
const STORAGE_V1 = "fastsell_cookie_consent_v1";

/** Shared across www / apex / *.usefastsell.com (not per-host localStorage). */
const CONSENT_COOKIE = "fastsell_consent_v2";
const CONSENT_MAX_AGE_SEC = 60 * 60 * 24 * 400; // ~13 months

function getConsentCookieDomain(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) return null;
  if (host.endsWith(".netlify.app")) return null;
  if (host === "usefastsell.com" || host.endsWith(".usefastsell.com")) return ".usefastsell.com";
  return null;
}

function parseConsentPayload(raw: string): GranularConsent | null {
  try {
    const o = JSON.parse(raw) as Partial<GranularConsent>;
    if (typeof o.analytics === "boolean" && typeof o.marketing === "boolean") {
      return { analytics: o.analytics, marketing: o.marketing };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function readConsentCookie(): GranularConsent | null {
  if (typeof document === "undefined") return null;
  const pref = `${CONSENT_COOKIE}=`;
  const parts = document.cookie.split("; ");
  for (const p of parts) {
    if (p.startsWith(pref)) {
      const encoded = p.slice(pref.length);
      try {
        return parseConsentPayload(decodeURIComponent(encoded));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeConsentCookie(c: GranularConsent): void {
  if (typeof document === "undefined") return;
  const domain = getConsentCookieDomain();
  const value = encodeURIComponent(JSON.stringify(c));
  let chunk = `${CONSENT_COOKIE}=${value}; path=/; max-age=${CONSENT_MAX_AGE_SEC}; SameSite=Lax`;
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    chunk += "; Secure";
  }
  if (domain) chunk += `; Domain=${domain}`;
  document.cookie = chunk;
}

export function readGranularConsent(): GranularConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY_V2);
    if (raw) {
      const parsed = parseConsentPayload(raw);
      if (parsed) {
        try {
          if (!readConsentCookie()) writeConsentCookie(parsed);
        } catch {
          /* ignore */
        }
        return parsed;
      }
    }
    const fromCookie = readConsentCookie();
    if (fromCookie) {
      try {
        localStorage.setItem(CONSENT_STORAGE_KEY_V2, JSON.stringify(fromCookie));
      } catch {
        /* localStorage peut être bloqué ; le cookie suffit */
      }
      return fromCookie;
    }
    const v1 = localStorage.getItem(STORAGE_V1);
    if (v1 === "accepted") {
      const g: GranularConsent = { analytics: true, marketing: true };
      try {
        localStorage.setItem(CONSENT_STORAGE_KEY_V2, JSON.stringify(g));
        localStorage.removeItem(STORAGE_V1);
      } catch {
        /* ignore */
      }
      try {
        writeConsentCookie(g);
      } catch {
        /* ignore */
      }
      return g;
    }
    if (v1 === "rejected") {
      const g: GranularConsent = { analytics: false, marketing: false };
      try {
        localStorage.setItem(CONSENT_STORAGE_KEY_V2, JSON.stringify(g));
        localStorage.removeItem(STORAGE_V1);
      } catch {
        /* ignore */
      }
      try {
        writeConsentCookie(g);
      } catch {
        /* ignore */
      }
      return g;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function writeGranularConsent(c: GranularConsent): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY_V2, JSON.stringify(c));
    localStorage.removeItem(STORAGE_V1);
  } catch {
    /* localStorage plein / mode privé */
  }
  try {
    writeConsentCookie(c);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent<GranularConsent>("fastsell-consent-v2", { detail: c }));
  window.dispatchEvent(
    new CustomEvent<"accepted" | "rejected">("fastsell-cookie-consent", {
      detail: c.marketing && c.analytics ? "accepted" : "rejected",
    }),
  );
}

export function consentAllowsAnalytics(): boolean {
  return readGranularConsent()?.analytics === true;
}

export function consentAllowsMarketing(): boolean {
  return readGranularConsent()?.marketing === true;
}
